import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import { Questionnaire, QuestionnaireField, QuestionnaireFieldType, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';

interface QuestionnairesManagementProps {
  permissions: CrudPermissions;
}
import { v4 as uuidv4 } from 'uuid';

export const QuestionnairesManagement: React.FC<QuestionnairesManagementProps> = ({ permissions }) => {
  const { questionnaires, setQuestionnaires, questionnaireFields: fields, setQuestionnaireFields: setFields } = useData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingQuestionnaire, setEditingQuestionnaire] = useState<Questionnaire | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<Partial<Questionnaire>>({
    name: '',
    description: '',
  });
  const [formFields, setFormFields] = useState<Partial<QuestionnaireField>[]>([]);

  const filteredQuestionnaires = useMemo(() => {
    return questionnaires.filter((questionnaire) => {
      const matchesSearch =
        questionnaire.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (questionnaire.description?.toLowerCase() || '').includes(searchQuery.toLowerCase());

      return matchesSearch;
    });
  }, [questionnaires, searchQuery]);

  const handleAdd = () => {
    setEditingQuestionnaire(null);
    setFormData({
      name: '',
      description: '',
    });
    setFormFields([]);
    setOpenDialog(true);
  };

  const handleEdit = (questionnaire: Questionnaire) => {
    setEditingQuestionnaire(questionnaire);
    setFormData(questionnaire);
    const questionnaireFields = fields
      .filter((f) => f.questionnaireId === questionnaire.id)
      .sort((a, b) => a.fieldOrder - b.fieldOrder);
    setFormFields(questionnaireFields);
    setOpenDialog(true);
  };

  // Tour-driven edit demo (step 12). Cursor "clicks" the Spirit Wear
  // card's Edit button; this trigger does the actual open. Close the
  // dialog only when the step actually changes (detected via
  // stepId) — otherwise other triggers within the same step would
  // close it prematurely.
  useEffect(() => {
    let ownerStepId: string | null = null;
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const stepId: string | undefined = detail?.stepId;
      if (detail?.trigger === 'open-spirit-wear-edit') {
        ownerStepId = stepId ?? null;
        const spiritWear = questionnaires.find((q) => q.name === 'Spirit Wear Information');
        if (spiritWear) handleEdit(spiritWear);
      } else if (ownerStepId !== null && stepId !== ownerStepId) {
        ownerStepId = null;
        setOpenDialog(false);
      }
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => window.removeEventListener('cc-tour-trigger', onTrigger);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaires, fields]);

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this questionnaire?')) {
      setQuestionnaires(questionnaires.filter((q) => q.id !== id));
      setFields(fields.filter((f) => f.questionnaireId !== id));
      const qIndex = mockQuestionnaires.findIndex((q) => q.id === id);
      if (qIndex > -1) {
        mockQuestionnaires.splice(qIndex, 1);
      }
      const fieldIndexes = mockQuestionnaireFields
        .map((f, i) => (f.questionnaireId === id ? i : -1))
        .filter((i) => i !== -1);
      fieldIndexes.reverse().forEach((i) => mockQuestionnaireFields.splice(i, 1));
    }
  };

  const handleSave = () => {
    if (editingQuestionnaire) {
      const updated = questionnaires.map((q) =>
        q.id === editingQuestionnaire.id
          ? {
              ...q,
              ...formData,
              updatedAt: new Date(),
              fields: formFields as QuestionnaireField[],
            }
          : q
      );
      setQuestionnaires(updated);

      const qIndex = mockQuestionnaires.findIndex((q) => q.id === editingQuestionnaire.id);
      if (qIndex > -1) {
        mockQuestionnaires[qIndex] = {
          ...mockQuestionnaires[qIndex],
          ...formData,
          updatedAt: new Date(),
        } as Questionnaire;
      }

      const existingFieldIds = new Set(
        fields.filter((f) => f.questionnaireId === editingQuestionnaire.id).map((f) => f.id)
      );
      const newFields = fields.filter((f) => f.questionnaireId !== editingQuestionnaire.id);
      const updatedFields = formFields.map((f) => ({
        ...f,
        questionnaireId: editingQuestionnaire.id,
        id: f.id || uuidv4(),
        createdAt: f.createdAt || new Date(),
      })) as QuestionnaireField[];

      setFields([...newFields, ...updatedFields]);

      existingFieldIds.forEach((id) => {
        const fIndex = mockQuestionnaireFields.findIndex((f) => f.id === id);
        if (fIndex > -1) {
          mockQuestionnaireFields.splice(fIndex, 1);
        }
      });
      updatedFields.forEach((field) => {
        mockQuestionnaireFields.push(field);
      });
    } else {
      const newQuestionnaire: Questionnaire = {
        id: uuidv4(),
        name: formData.name!,
        description: formData.description,
        createdAt: new Date(),
        updatedAt: new Date(),
        fields: formFields as QuestionnaireField[],
      };
      setQuestionnaires([...questionnaires, newQuestionnaire]);
      mockQuestionnaires.push(newQuestionnaire);

      const newFields = formFields.map((f, index) => ({
        ...f,
        questionnaireId: newQuestionnaire.id,
        id: uuidv4(),
        fieldOrder: index,
        createdAt: new Date(),
      })) as QuestionnaireField[];

      setFields([...fields, ...newFields]);
      newFields.forEach((field) => {
        mockQuestionnaireFields.push(field);
      });
    }
    setOpenDialog(false);
  };

  const handleAddField = () => {
    setFormFields([
      ...formFields,
      {
        fieldName: '',
        fieldType: 'text',
        fieldOrder: formFields.length,
        required: false,
      },
    ]);
  };

  const handleUpdateField = (index: number, updates: Partial<QuestionnaireField>) => {
    const updated = [...formFields];
    updated[index] = { ...updated[index], ...updates };
    setFormFields(updated);
  };

  const handleDeleteField = (index: number) => {
    const updated = formFields.filter((_, i) => i !== index);
    updated.forEach((field, i) => {
      field.fieldOrder = i;
    });
    setFormFields(updated);
  };

  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    const updated = [...formFields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= updated.length) return;

    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated.forEach((field, i) => {
      field.fieldOrder = i;
    });
    setFormFields(updated);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Questionnaires Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage custom questionnaire forms
          </Typography>
        </Box>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add Questionnaire
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search questionnaires..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(auto-fill, minmax(320px, 1fr))',
          },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {filteredQuestionnaires.map((questionnaire) => {
          const questionnaireFields = fields
            .filter((f) => f.questionnaireId === questionnaire.id)
            .sort((a, b) => a.fieldOrder - b.fieldOrder);

          const isSpiritWear = questionnaire.name === 'Spirit Wear Information';
          return (
            <Box
              key={questionnaire.id}
              sx={{
                display: 'flex',
                minWidth: 0,
              }}
              {...(isSpiritWear ? { 'data-tour': 'questionnaire-card-spirit-wear' } : {})}
            >
              <Card
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    {questionnaire.name}
                  </Typography>
                  {questionnaire.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {questionnaire.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {questionnaireFields.map((field) => (
                      <Chip
                        key={field.id}
                        label={field.fieldName}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                  {permissions.update && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => handleEdit(questionnaire)}
                      {...(isSpiritWear ? { 'data-tour': 'questionnaire-edit-spirit-wear' } : {})}
                      sx={{ flexGrow: 1, minWidth: 0 }}
                    >
                      Edit
                    </Button>
                  )}
                  {permissions.delete && (
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(questionnaire.id)}
                      color="error"
                      sx={{ flexShrink: 0 }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Box>
          );
        })}
      </Box>

      {filteredQuestionnaires.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No questionnaires found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? 'Try adjusting your search'
              : 'Click "Add Questionnaire" to create your first questionnaire'}
          </Typography>
        </Box>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { 'data-tour': 'questionnaire-edit-dialog' } as React.HTMLAttributes<HTMLDivElement> }}
      >
        <DialogTitle>
          {editingQuestionnaire ? 'Edit Questionnaire' : 'Add New Questionnaire'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Questionnaire Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <Divider />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Fields
              </Typography>
              <Button size="small" startIcon={<Add />} onClick={handleAddField}>
                Add Field
              </Button>
            </Box>

            {formFields.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No fields added yet. Click "Add Field" to create a field.
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {formFields.map((field, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 2,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        mb: 2,
                        p: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="Field Name"
                          value={field.fieldName}
                          onChange={(e) =>
                            handleUpdateField(index, { fieldName: e.target.value })
                          }
                          fullWidth
                          required
                          size="small"
                        />
                        <TextField
                          label="Field Type"
                          value={field.fieldType}
                          onChange={(e) =>
                            handleUpdateField(index, {
                              fieldType: e.target.value as QuestionnaireFieldType,
                            })
                          }
                          select
                          sx={{ minWidth: 150 }}
                          size="small"
                        >
                          <MenuItem value="text">Text</MenuItem>
                          <MenuItem value="multiselect">Multi-Select</MenuItem>
                        </TextField>
                      </Box>

                      {field.fieldType === 'multiselect' && (
                        <TextField
                          label="Options (comma-separated)"
                          value={field.options || ''}
                          onChange={(e) => handleUpdateField(index, { options: e.target.value })}
                          fullWidth
                          placeholder="Option 1, Option 2, Option 3"
                          size="small"
                          helperText="Enter options separated by commas"
                        />
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.required}
                              onChange={(e) =>
                                handleUpdateField(index, { required: e.target.checked })
                              }
                              size="small"
                            />
                          }
                          label="Required"
                        />
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveField(index, 'up')}
                            disabled={index === 0}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveField(index, 'down')}
                            disabled={index === formFields.length - 1}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteField(index)}
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
